import React, { FC, useState } from 'react'
import { Grid, Paper, TextField } from '@mui/material'
import { DataGridPremium, GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium'
import { Sensor } from 'src/api'
import { TableToolbar } from 'src/components/ui'
import { SensorColumnsDataGrid } from 'src/components/columns/sensor'
import { useSensorUploadSensorsListRequest, SensorUploadSensorsType } from 'src/hooks/api'
import { useDebouncedValue } from 'src/hooks/ui'
import { defaultRowsValuePerPage } from 'src/config'

const defaultPerPage = 50

const getColumns = (type: SensorUploadSensorsType): GridColDef<Sensor>[] => [
  ...(type === 'skipped' ? [SensorColumnsDataGrid.UploadOrderNumber] : []),
  SensorColumnsDataGrid.Id(),
  SensorColumnsDataGrid.Model,
  SensorColumnsDataGrid.IMEI,
  SensorColumnsDataGrid.DevEUI,
  SensorColumnsDataGrid.TypeName,
  SensorColumnsDataGrid.State,
  SensorColumnsDataGrid.DateAdded,
  SensorColumnsDataGrid.OrderNumber,
  SensorColumnsDataGrid.OrderDate,
  ...(type === 'imported' ? [SensorColumnsDataGrid.OrderPart] : [])
]

interface SensorUploadSensorsProps {
  uploadId: number
  type: SensorUploadSensorsType
}

export const SensorUploadSensors: FC<SensorUploadSensorsProps> = ({ uploadId, type }) => {
  const [orderNumber, setOrderNumber] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(defaultPerPage)
  const debouncedOrderNumber = useDebouncedValue(orderNumber)
  const debouncedSearch = useDebouncedValue(search)

  const { data, isInitialLoading, isLoading } = useSensorUploadSensorsListRequest({
    uploadId,
    type,
    params: {
      limit: pageSize,
      offset: page * pageSize,
      ...(debouncedOrderNumber ? { orderNumber: debouncedOrderNumber } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {})
    },
    options: { keepPreviousData: true }
  })

  const handleChangePaginationModel = (pagination: GridPaginationModel): void => {
    setPage(pagination.page)
    setPageSize(pagination.pageSize)
  }

  const handleOrderNumberChange = (value: string): void => {
    setOrderNumber(value)
    setPage(0)
  }

  const handleSearchChange = (value: string): void => {
    setSearch(value)
    setPage(0)
  }

  return (
    <Grid container direction="column" wrap="nowrap" spacing={2}>
      <Grid item container spacing={2}>
        <Grid item xs={12} sm="auto">
          <TextField label="Order number" size="small" value={orderNumber} onChange={(event) => handleOrderNumberChange(event.target.value)} sx={{ width: { xs: '100%', sm: 220 } }} />
        </Grid>
        <Grid item xs={12} sm="auto">
          <TextField
            label="Search by ID / IMEI / Dev EUI"
            size="small"
            value={search}
            onChange={(event) => handleSearchChange(event.target.value)}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />
        </Grid>
      </Grid>
      <Grid item>
        <Paper sx={{ height: '600px', width: '100%' }}>
          <DataGridPremium<Sensor>
            pagination
            disableRowSelectionOnClick
            rows={data?.rows || []}
            columns={getColumns(type)}
            rowCount={data?.count || 0}
            loading={isInitialLoading || isLoading}
            paginationModel={{ page, pageSize }}
            rowThreshold={2}
            columnThreshold={2}
            paginationMode="server"
            pageSizeOptions={defaultRowsValuePerPage}
            onPaginationModelChange={handleChangePaginationModel}
            localeText={{ columnMenuSortAsc: 'Sort A-Z', columnMenuSortDesc: 'Sort Z-A' }}
            slots={{ toolbar: () => <TableToolbar exportedFileName={`${document.title}_${type}_sensors`} /> }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: 1,
                fontSize: '13px',
                fontWeight: 400,
                color: 'rgba(0, 0, 0, 0.6)',
                overflow: 'visible'
              }
            }}
          />
        </Paper>
      </Grid>
    </Grid>
  )
}
